import React, {useEffect, useState} from "react";
import {getUserPartnerPromoCodeApi, requestPartnerPromoCodeApi} from "../../http/affiliateAPI";
import { Form, Button, Alert, Spinner } from "react-bootstrap";
import { useContext } from "react";
import { Context } from "../../index";
import {toast} from "react-toastify";


const isTechnicalResourceError = (message = "") =>
    /out of shared memory|max_locks_per_transaction|could not resize shared memory/i.test(String(message));


const RequestPartnerPromoCode = () => {
    const { user, affiliateStore } = useContext(Context);
    const [attractedCount, setAttractedCount] = useState(0);
    const discountOptions = [10, 15, 20, 25, 30, 35, 40, 45, 50];
    const codeTypeOptions = [
        { value: "One_Month", label: "1 Month" },
        { value: "Three_Months", label: "3 Months" },
        { value: "Six_Months", label: "6 Months" },
        { value: "One_Year", label: "1 Year" },
        { value: "Forever", label: "Forever" }
    ];
    const validUntilOptions = [
        { value: 1, label: "1 Month from now" },
        { value: 3, label: "3 Months from now" },
        { value: 6, label: "6 Months from now" },
        { value: 12, label: "1 Year from now" },
        { value: 36, label: "3 Years from now" }
    ];
    const getAvailableOptions = (attractedCount) => {
        if (attractedCount >= 50) {
            return {
                discount: discountOptions.filter(v => v <= 50),
                duration: codeTypeOptions,
                validUntil: validUntilOptions
            };
        } else if (attractedCount >= 30) {
            return {
                discount: discountOptions.filter(v => v <= 40),
                duration: codeTypeOptions.filter(opt => opt.value !== "Forever"),
                validUntil: validUntilOptions.filter(opt => opt.value <= 12)
            };
        } else if (attractedCount >= 15) {
            return {
                discount: discountOptions.filter(v => v <= 30),
                duration: codeTypeOptions.filter(opt => ["One_Month", "Three_Months", "Six_Months"].includes(opt.value)),
                validUntil: validUntilOptions.filter(opt => opt.value <= 6)
            };
        } else if (attractedCount >= 5) {
            return {
                discount: discountOptions.filter(v => v <= 20),
                duration: codeTypeOptions.filter(opt => ["One_Month", "Three_Months"].includes(opt.value)),
                validUntil: validUntilOptions.filter(opt => opt.value <= 3)
            };
        } else {
            return {
                discount: discountOptions.filter(v => v <= 10),
                duration: codeTypeOptions.filter(opt => opt.value === "One_Month"),
                validUntil: validUntilOptions.filter(opt => opt.value === 1)
            };
        }
    };
    const availableOptions = getAvailableOptions(attractedCount);



    const [formData, setFormData] = useState({
        desired_code: "",
        discount_percent: 10,
        code_type: "One_Month",
        valid_until_months: 1,
        request_comment: ""
    });

    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const [promoRequests, setPromoRequests] = useState([]);
    const [loadingRequests, setLoadingRequests] = useState(true);


    useEffect(() => {
        const fetchRequests = async () => {
            try {
                if (user?.user?.id) {
                    const data = await getUserPartnerPromoCodeApi(user.user.id);
                    const codes = Array.isArray(data?.codes) ? data.codes : [];
                    setPromoRequests(codes);
                    affiliateStore.setPromoRequests(codes);
                    setAttractedCount(Number(data?.attractedCount) || 0);
                    if (data?.warning) {
                        toast.info(data.warning, { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
                    }
                }
            } catch (e) {
                const rawMessage = e?.response?.data?.message || e?.message || "";
                const message = isTechnicalResourceError(rawMessage)
                    ? "Promo code data is temporarily unavailable. Please refresh in a moment."
                    : rawMessage || "Failed to load promo code requests";
                toast.error(message, { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            } finally {
                setLoadingRequests(false);
            }
        };

        fetchRequests();
    }, [user, affiliateStore, loading]);

    const formatValidUntil = (monthsToAdd) => {
        const date = new Date();
        date.setMonth(date.getMonth() + Number(monthsToAdd));
        date.setHours(23, 59, 59, 0);
        return date.toISOString();
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        let formattedValue = value;

        if (name === "desired_code") {
            // Удаляем всё, что не A-Z или 0-9
            formattedValue = value
                .toUpperCase()
                .replace(/[^A-Z0-9]/g, '');
        }

        setFormData((prev) => ({
            ...prev,
            [name]: formattedValue,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!user?.user?.id) return;

        setLoading(true);
        setResult(null);

        try {
            const response = await requestPartnerPromoCodeApi({
                user_id: user.user.id,
                desired_code: formData.desired_code.trim(),
                discount_percent: Number(formData.discount_percent),
                code_type: formData.code_type,
                valid_until: formatValidUntil(formData.valid_until_months),
                request_comment: formData.request_comment.trim()
            });

            if (response.message) {
                toast.info(response.message, { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            } else {
                toast.info("Request submitted!", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            }

            setFormData({
                desired_code: "",
                discount_percent: 10,
                code_type: "One_Month",
                valid_until_months: 1,
                request_comment: ""
            });

            setResult({ type: "success", msg: response.message || "Request submitted!" });
        } catch (e) {
            const message = e.response?.data?.message || "Error submitting request";
            setResult({ type: "danger", msg: message });
            toast.error(message, { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-3 rounded">
            <h6 className="text-success mb-2">Want to offer your own promo code? Apply now!</h6>
            <p className="text-muted small mb-3">
                If you're an active partner or community leader, you can request your own custom promo code.
                Choose how long the code will be active, what discount it gives, and until when it's valid.
                We'll review and activate it shortly.
            </p>
            {result && (
                <Alert variant={result.type} className="small py-2">
                    {result.msg}
                </Alert>
            )}

            <h6 className=" text-success mb-2">Agenda to Promo Codes Ordering:</h6>
            <div className="text-muted small mb-3">
                <div><strong>Desired Promo Code</strong> – this is the custom code you’d like to offer users, such as <code>SPRING2025</code>. It should be short and easy to remember.</div>
                <div><strong>Discount %</strong> – this is how much discount users will receive when they use your code. For example, 25% means users pay 25% less.</div>
                <div><strong>Promo Duration</strong> – this controls how long the discount stays active after the user subscribes. Choose between 1 month, several months, or forever.</div>
                <div><strong>Last Usable Date</strong> – this sets a deadline after which the code won’t work anymore. Think of it like an expiration date.</div>
                <div><strong>Promotion Context / Notes</strong> – briefly explain how and where you plan to share this promo code (e.g. YouTube, newsletter, community).</div>
                <div className="mt-2"><strong>Note:</strong> Increasing the discount percentage significantly reduces your potential earnings. Payouts are calculated based on the formula: <em>user payment amount – payment system fee × your commission rate</em>.</div>



                <div className="mt-2"><strong>Important:</strong> If any user who currently has no referrer uses your promo code, they will be automatically assigned to you, and you’ll receive bonuses according to your current reward model.</div>
            </div>

            <Form className="d-flex align-items-end gap-3 fst-italic flex-wrap" onSubmit={handleSubmit}>
                <Form.Group>
                    <Form.Label className="small text-muted mb-0">Desired Promo Code:</Form.Label>
                    <Form.Control
                        size="sm"
                        name="desired_code"
                        placeholder="SPRING2025"
                        value={formData.desired_code}
                        onChange={handleChange}
                        required
                    />
                </Form.Group>

                <Form.Group>
                    <Form.Label className="small text-muted mb-0">Discount %</Form.Label>
                    <Form.Select
                        size="sm"
                        name="discount_percent"
                        value={formData.discount_percent}
                        onChange={handleChange}
                        className="small text-muted"
                    >
                        {availableOptions.discount.map((value) => (
                            <option key={value} value={value} className="text-muted">{value}%</option>
                        ))}
                    </Form.Select>
                </Form.Group>

                <Form.Group>
                    <Form.Label className="small text-muted mb-0">Promo Duration:</Form.Label>
                    <Form.Select
                        size="sm"
                        name="code_type"
                        value={formData.code_type}
                        onChange={handleChange}
                        className="small text-muted"
                    >
                        {availableOptions.duration.map((opt) => (
                            <option key={opt.value} value={opt.value} className="text-muted">{opt.label}</option>
                        ))}
                    </Form.Select>
                </Form.Group>

                <Form.Group>
                    <Form.Label className="small text-muted mb-0">Last Usable Date:</Form.Label>
                    <Form.Select
                        size="sm"
                        name="valid_until_months"
                        value={formData.valid_until_months}
                        onChange={handleChange}
                        className="small text-muted"
                    >
                        {availableOptions.validUntil.map((opt) => (
                            <option key={opt.value} value={opt.value} className="text-muted">{opt.label}</option>
                        ))}
                    </Form.Select>
                </Form.Group>



                <Form.Group className="flex-grow-1">
                    <Form.Label className="small text-muted mb-0">Promotion Context / Notes:</Form.Label>
                    <Form.Control
                        size="sm"
                        name="request_comment"
                        placeholder="Where and how you'll promote it"
                        value={formData.request_comment}
                        onChange={handleChange}
                        required
                    />
                </Form.Group>

                <Button type="submit" size="sm" className="btn-success" disabled={loading}>
                    {loading ? <Spinner size="sm" animation="border" /> : "Submit Request"}
                </Button>
            </Form>
            <h6 className="text-muted small mt-4">Your Submitted Promo Code Requests:</h6>
            {loadingRequests ? (
                <div className="text-muted small">Loading...</div>
            ) : promoRequests.length === 0 ? (
                <div className="text-muted small">No promo code requests yet.</div>
            ) : (
                <div className="table-responsive">
                    <table className="table table-sm table-bordered table-striped shadow-sm align-middle text-center small"
                           style={{ maxWidth: '100%', lineHeight: '14px',  whiteSpace: "nowrap", }}>
                        <thead className="table-light">
                        <tr className="fst-italic small">
                            <th className="text-muted">ID</th>
                            <th className="text-muted">Order Date</th>
                            <th className="text-muted">Desired Code</th>
                            <th className="text-muted">Discount</th>
                            <th className="text-muted">Duration</th>
                            <th className="text-muted">Order Status</th>
                            <th className="text-muted">Code Status</th>
                            <th className="text-muted">Code</th>
                            <th className="text-muted">Valid Until</th>
                            <th className="text-muted">Approve Date</th>
                            <th className="text-muted">Comment</th>
                        </tr>
                        </thead>
                        <tbody>
                        {affiliateStore.promoRequests.map((req) => (
                            <tr key={req.id} className="small">
                                <td className="text-muted">{req.id}</td>
                                <td className="text-muted">
                                    {req.order_date
                                    ? new Date(req.order_date).toLocaleDateString('en-GB', {
                                        day: '2-digit',
                                        month: 'short',
                                        year: 'numeric'
                                    }).replace(/ /g, '-')
                                    : "-"}
                                </td>
                                <td className="text-muted">{req.desired_code}</td>

                                <td className="text-muted">{req.discount_percent}%</td>
                                <td className="text-muted">{req.code_type || "-"}</td>

                                <td className="text-muted">
                                  <span className={`badge me-1 bg-${req.order_status === "approved" ? "success" : req.order_status === "rejected" ? "danger" : "secondary bg-opacity-75"}`}>
                             {req.order_status}
                                 </span>

                                                       </td>

                                <td className="text-muted">
                            <span className={`badge bg-${req.promo_code_status === "active"
                                  ? "success"
                              : req.promo_code_status === "used" || req.promo_code_status === "expired"
                               ? "danger"
                               : "secondary bg-opacity-75"
                             }`}>
                               {req.promo_code_status}
                                 </span>
                                </td>
                                <td className="text-muted fw-bolder">{req.code}</td>
                                <td className="text-muted">
                                    {req.order_status === "approved" && req.valid_until
                                        ? new Date(req.valid_until).toLocaleDateString('en-GB', {
                                            day: '2-digit',
                                            month: 'short',
                                            year: 'numeric'
                                        }).replace(/ /g, '-')
                                        : "-"}
                                </td>

                                <td className="text-muted">
                                    {req.approval_date
                                        ? new Date(req.approval_date).toLocaleDateString('en-GB', {
                                            day: '2-digit',
                                            month: 'short',
                                            year: 'numeric'
                                        }).replace(/ /g, '-')
                                        : "-"}
                                </td>
                                <td className="text-muted">{req.admin_comment || "-"}</td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}
            <div className="mt-4 p-3 border small rounded bg-light">
                <strong>Dear Partner,</strong><br />
                We truly appreciate your interest in promoting Virtalibry.
                Currently, we see that you have <strong>{attractedCount || 0} referred users</strong> in your account.

                <p className="text-muted mt-3 mb-2">
                    To ensure our system remains fair and rewarding for everyone, we use a tiered model for promo codes.
                    These are growth-friendly guidelines — designed to reward long-term partners and maintain equal opportunity across the platform.
                    As you bring more users, your privileges increase automatically.
                </p>

                <div className="table-responsive mt-2 small">
                    <table className="table table-sm table-striped table-bordered mb-0 mx-auto text-center"
                           style={{ lineHeight: '14px',  whiteSpace: "nowrap", maxWidth: "450px", width: "100%" }}>
                        <thead>
                        <tr >
                            <th scope="col" className="text-muted ">Referred Users</th>
                            <th scope="col" className="text-muted ">Max Discount</th>
                            <th scope="col" className="text-muted ">Max Duration</th>
                            <th scope="col" className="text-muted">Max Last Usable Date</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr>
                            <td>0</td>
                            <td>10%</td>
                            <td>1 Month</td>
                            <td>1 Month from now</td>
                        </tr>
                        <tr>
                            <td>5+</td>
                            <td>20%</td>
                            <td>3 Months</td>
                            <td>3 Months from now</td>
                        </tr>
                        <tr>
                            <td>15+</td>
                            <td>30%</td>
                            <td>6 Months</td>
                            <td>6 Months from now</td>
                        </tr>
                        <tr>
                            <td>30+</td>
                            <td>40%</td>
                            <td>1 Year</td>
                            <td>1 Year from now</td>
                        </tr>
                        <tr>
                            <td>50+</td>
                            <td>50%</td>
                            <td>Forever</td>
                            <td>3 Years from now</td>
                        </tr>
                        </tbody>
                    </table>
                </div>

                <div className="text-muted mt-3">
                    <strong>After you submit your promo code request</strong>, you’ll receive an email from us asking you to briefly introduce yourself and share how you plan to use the promo code. Please take a moment to reply — this will help us process your request faster.
                    <br />
                    <strong>And if you believe that</strong> someone at your level absolutely deserves a promo code under special conditions — we welcome that! Just drop us a few lines in your response.
                    The more we understand your goals, the better we can support you.
                </div>
            </div>

        </div>
    );
};

export default RequestPartnerPromoCode;
